import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@/types/auth';
import {
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  loginWithGoogle as apiLoginWithGoogle,
  getMe,
  storeTokens,
  clearTokens,
  getStoredToken,
} from '@/services/authApi';
import { AuthContext } from './AuthContext';

// Split out of AuthContext.tsx (react-refresh/only-export-components): that
// file exports the `useAuth` hook, which is imported from dozens of call
// sites app-wide, so it stays put. `AuthProvider` has exactly one production
// import site (main.tsx) plus test wrappers, so it moved instead — far
// fewer call sites to update. Behavior is unchanged; only the file boundary
// moved.
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // On mount: validate any existing token by fetching the current user
  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    getMe()
      .then((fetchedUser) => {
        setUser(fetchedUser);
      })
      .catch(() => {
        // Token is invalid or expired — clear it
        clearTokens();
        setUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const tokens = await apiLogin({ email, password });
    storeTokens(tokens);
    const fetchedUser = await getMe();
    setUser(fetchedUser);
  }, []);

  const register = useCallback(
    async (
      email: string,
      password: string,
      displayName: string | undefined,
      role: 'coach' | 'athlete'
    ) => {
      const tokens = await apiRegister({
        email,
        password,
        display_name: displayName,
        role,
      });
      storeTokens(tokens);
      const fetchedUser = await getMe();
      setUser(fetchedUser);
    },
    []
  );

  const refreshUser = useCallback(async () => {
    const fetchedUser = await getMe();
    setUser(fetchedUser);
  }, []);

  const loginWithGoogle = useCallback(async (credential: string) => {
    // apiLoginWithGoogle() already calls storeTokens() before returning.
    await apiLoginWithGoogle(credential);
    const fetchedUser = await getMe();
    setUser(fetchedUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      // apiLogout() POSTs to /api/auth/logout so the backend clears the
      // httpOnly refresh-token cookie. It also calls clearTokens() internally.
      await apiLogout();
    } catch {
      // Swallow API errors — network failures, 5xx, etc. We still need to
      // clear local state and redirect. Do not let the caller receive an error
      // from a logout operation; the user must always reach /login.
    } finally {
      // Always clear local user state and redirect — even if the API call
      // failed (network error, server down). We must not trap the user.
      setUser(null);
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const isAuthenticated = user !== null;

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated, login, register, logout, refreshUser, loginWithGoogle }}>
      {children}
    </AuthContext.Provider>
  );
}
